import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { AutumnPointBackCallResultOverlay } from "~/components/autumnPointBack/AutumnPointBackCallResultOverlay";
import { BottomNav } from "~/components/BottomNav";
import { CallPointShortagePromptHost } from "~/components/CallPointShortagePrompt";
import { ErrorModal } from "~/components/ErrorModal";
import { HokageMigrationNoticeModal } from "~/components/hokageMigration/HokageMigrationNoticeModal";
import { MediaGalleryGuideHost } from "~/components/MediaGalleryGuide";
import { MediaNextVideoPromptHost } from "~/components/MediaNextVideoPrompt";
import { MediaPointShortagePromptHost } from "~/components/MediaPointShortagePrompt";
import { PointShortagePromptHost } from "~/components/PointShortagePrompt";
import { PreroutePromptHost } from "~/components/PreroutePromptHost";
import { PushSoftAskSheet } from "~/components/pushSoftAsk/PushSoftAskSheet";
import {
  MIN_POINT_FOR_VIDEO_CALL,
  MIN_POINT_FOR_VOICE_CALL,
} from "~/constants/actionsPoint";
import { CALL_TYPE } from "~/constants/call";
import { TEXT } from "~/constants/text";
import { CallEndedModal } from "~/features/call/components/CallEndedModal";
import { CallOverlay } from "~/features/call/components/CallOverlay";
import { CocoplusCallEndedModal } from "~/features/call/components/CocoplusCallEndedModal";
import { useCallCleanup } from "~/features/call/hooks/useCallCleanup";
import { useCallFallbackActions } from "~/features/call/hooks/useCallFallbackActions";
import { useCocoplusCallEndedFavorite } from "~/features/call/hooks/useCocoplusCallEndedFavorite";
import { mapCallEndReasonToEvent } from "~/features/call/utils/callEndEvent";
import { DemoCallOverlay } from "~/features/demoCall/components/DemoCallOverlay";
import { NewsModal } from "~/features/news/components/NewsModal";
import { CallLowPointSheet } from "~/features/purchase/components/CallLowPointSheet";
import { InsufficientPointNotice } from "~/features/purchase/components/InsufficientPointNotice";
import { InsufficientPointSheet } from "~/features/purchase/components/InsufficientPointSheet";
import { PurchaseQuickChargeSheet } from "~/features/purchase/components/PurchaseQuickChargeSheet";
import { useNotificationPolling } from "~/features/settings/notifications/hooks/useNotificationPolling";
import { SignupBonusModal } from "~/features/signupBonus/SignupBonusModal";
import { useDelayedUnmount } from "~/hooks/useDelayedUnmount";
import { useHasPurchased } from "~/hooks/useHasPurchased";
import { useMediaNextVideoPromptDwell } from "~/hooks/useMediaNextVideoPromptDwell";
import { usePrefetchGiftImages } from "~/hooks/usePrefetchGiftImages";
import { usePreroutePromptDwell } from "~/hooks/usePreroutePromptDwell";
import { useResyncFirebaseUserId } from "~/hooks/useResyncFirebaseUserId";
import { useRtmResidentLogin } from "~/hooks/useRtmResidentLogin";
import { useSendCallRequest } from "~/hooks/useSendCallRequest";
import { useSyncCocoplusDeviceToken } from "~/hooks/useSyncCocoplusDeviceToken";
import { useSyncNotificationTokenOnPermissionChange } from "~/hooks/useSyncNotificationTokenOnPermissionChange";
import { isCocoplusApp } from "~/lib/applicationId";
import { isOnCallNow } from "~/lib/callActivity";
import { getAvatarUrl } from "~/lib/imageUrl";
import {
  FIREBASE_EVENT_NAME,
  trackFirebaseEvent,
} from "~/lib/tracking/firebase";
import { useAuthStore } from "~/stores/useAuthStore";
import { useAutumnPointBackCallResultStore } from "~/stores/useAutumnPointBackCallResultStore";
import { useCallLowPointSheetStore } from "~/stores/useCallLowPointSheetStore";
import { useCallPointShortagePromptStore } from "~/stores/useCallPointShortagePromptStore";
import { useCallStore } from "~/stores/useCallStore";
import { useInsufficientPointNoticeStore } from "~/stores/useInsufficientPointNoticeStore";
import { useInsufficientPointSheetStore } from "~/stores/useInsufficientPointSheetStore";
import { useLastNavTabStore } from "~/stores/useLastNavTabStore";
import { useMediaNextVideoPromptStore } from "~/stores/useMediaNextVideoPromptStore";
import { useMediaPointShortagePromptStore } from "~/stores/useMediaPointShortagePromptStore";
import { useNewsModalStore } from "~/stores/useNewsModalStore";
import { usePointShortagePromptStore } from "~/stores/usePointShortagePromptStore";
import { usePreroutePromptStore } from "~/stores/usePreroutePromptStore";
import { useQuickChargeStore } from "~/stores/useQuickChargeStore";
import { useSignupBonusStore } from "~/stores/useSignupBonusStore";
import { isCallPointShortagePromptTargetWindow } from "~/utils/callPointShortagePrompt";
import { markPointShortageCall } from "~/utils/lastPointShortageCall";
import { markPurchaseSurfaceSeen } from "~/utils/purchaseSurfaceSeen";
import {
  getComebackPushTier,
  isComebackPurchasePush,
  type PushTappedDetail,
} from "~/utils/pushTapPayload";

const AuthenticatedLayout = () => {
  useNotificationPolling();
  useCallCleanup();
  useRtmResidentLogin();
  // 通話/ライブ後レビューのギフト画像を起動後アイドル時に先読みし、初回表示のもたつきを防ぐ
  usePrefetchGiftImages();
  // セッション復元で LOGIN_SUCCESS を経由しなかったユーザーの Firebase user_id を補完通知する
  useResyncFirebaseUserId();
  // 同様に、セッション復元/自動ログインで古くなりうる coco+ のプッシュ通知トークンを最新化する
  useSyncCocoplusDeviceToken();
  const { location } = useRouterState();
  const navigate = useNavigate();
  const phase = useCallStore((s) => s.phase);
  const partnerName = useCallStore((s) => s.partnerName);
  const callType = useCallStore((s) => s.callType);
  // ストアにはアバター ID が入るため、表示用に URL へ変換する
  const partnerAvatarId = useCallStore((s) => s.partnerAvatarUrl);
  const partnerAvatarUrl = getAvatarUrl(partnerAvatarId);
  const partnerId = useCallStore((s) => s.partnerId);
  const { sendCallRequest, isPending: isSendingCallRequest } =
    useSendCallRequest();
  // 発信が無応答・拒否で終わったときだけ、終了モーダルに他の女性を出す
  const callFallback = useCallFallbackActions();
  const newsModalOpen = useNewsModalStore((s) => s.isOpen);
  const signupBonusOpen = useSignupBonusStore((s) => s.isOpen);
  const quickChargeOpen = useQuickChargeStore((s) => s.open);
  const quickChargeSource = useQuickChargeStore((s) => s.source);
  const quickChargeScene = useQuickChargeStore((s) => s.scene);
  const quickChargeOnSuccess = useQuickChargeStore((s) => s.onChargeSuccessCb);
  const hideQuickCharge = useQuickChargeStore((s) => s.hide);
  const insufficientSheetOpen = useInsufficientPointSheetStore((s) => s.open);
  const insufficientSheetNeeded = useInsufficientPointSheetStore(
    (s) => s.needed,
  );
  const insufficientSheetOnClose = useInsufficientPointSheetStore(
    (s) => s.onCloseCb,
  );
  const insufficientSheetOnSuccess = useInsufficientPointSheetStore(
    (s) => s.onChargeSuccessCb,
  );
  const insufficientSheetShakeSignal = useInsufficientPointSheetStore(
    (s) => s.shakeSignal,
  );
  const hideInsufficientSheet = useInsufficientPointSheetStore((s) => s.hide);
  // Coco+ の通話中 pt 不足シート（残り 60 秒を切ったとき）
  const callLowPointOpen = useCallLowPointSheetStore((s) => s.open);
  const callLowPointScene = useCallLowPointSheetStore((s) => s.scene);
  const callLowPointOnSuccess = useCallLowPointSheetStore(
    (s) => s.onChargeSuccessCb,
  );
  const hideCallLowPoint = useCallLowPointSheetStore((s) => s.hide);
  const insufficientNoticeVisible = useInsufficientPointNoticeStore(
    (s) => s.visible,
  );
  const insufficientNoticeOnTap = useInsufficientPointNoticeStore(
    (s) => s.onTapCb,
  );
  const hideInsufficientNotice = useInsufficientPointNoticeStore((s) => s.hide);
  // 通話 pt不足の文脈モーダルの対象判定。
  // 課金経験は BE 問い合わせなので、通話が切れた瞬間に間に合うよう、
  // 窓の内側にいるユーザーでは先に引いておく（対象外のユーザーでは発火しない）。
  const callPromptWindow = isCallPointShortagePromptTargetWindow(
    useAuthStore((s) => s.userId) ?? "",
  );
  const hasPurchased = useHasPurchased(callPromptWindow);
  const isCallPromptTarget = callPromptWindow && hasPurchased === false;
  const ptPromptDisplayed = usePointShortagePromptStore((s) => s.displayed);
  const callPromptContext = useCallPointShortagePromptStore((s) => s.context);
  const mediaPromptContext = useMediaPointShortagePromptStore((s) => s.context);
  const nextVideoPromptContext = useMediaNextVideoPromptStore((s) => s.context);
  // 「他の動画を見る」先回りシート: 1 本目の動画のあとアプリに 1 分とどまったら出す
  useMediaNextVideoPromptDwell();
  // ルート進入前の先回りモーダル: 最後の対象行動から 2 分止まったら出す
  usePreroutePromptDwell();
  // 通知権限が変わったら push トークンを登録し、変化を計測する
  useSyncNotificationTokenOnPermissionChange();
  const prerouteContext = usePreroutePromptStore((s) => s.context);
  // 閉じても slide-down 退場アニメのぶんだけマウントを維持する。
  const quickChargeRender = useDelayedUnmount(quickChargeOpen);
  const insufficientSheetRender = useDelayedUnmount(insufficientSheetOpen);
  const insufficientNoticeRender = useDelayedUnmount(insufficientNoticeVisible);
  const callLowPointRender = useDelayedUnmount(callLowPointOpen);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<PushTappedDetail>).detail;
      const { userid, noti_type } = detail;
      // noti_type 整備完了までは "noti_type_<値>" 形式で送る（値未提供時は unknown）。
      // パラメータ名は BQ 側クエリ互換のため campaign_id のまま変えない
      trackFirebaseEvent(FIREBASE_EVENT_NAME.PUSH_OPEN, {
        campaign_id: `noti_type_${noti_type ?? "unknown"}`,
      });
      if (isComebackPurchasePush(detail)) {
        trackFirebaseEvent(FIREBASE_EVENT_NAME.OKAERI_PUSH_OPEN, {
          tier: getComebackPushTier(detail),
        });
        // おかえりダイアログを出すかは「さがす」側が判定するので着地させるだけ
        if (isOnCallNow()) return;
        navigate({ to: "/discover" });
        return;
      }
      if (!userid) return;
      // 通話中・ライブ視聴中はメッセージ画面へ割り込ませない
      if (isOnCallNow()) return;
      navigate({
        to: "/messages/$conversationId",
        params: { conversationId: userid },
      });
    };
    window.addEventListener("pushTapped", handler);
    return () => window.removeEventListener("pushTapped", handler);
  }, [navigate]);

  useEffect(() => {
    // location.pathname はミュータブル値のため deps に含めず、effect 本体内で参照する
    const pathname = location.pathname;
    if (
      phase.type === "ended" &&
      !pathname.startsWith("/call") &&
      phase.reason === "pointShortage"
    ) {
      // ここから先は必ず購入導線（文脈モーダル or 汎用シート）を出すので、
      // チャットの先回りモーダルの抑止用に記録する。イベントは変えない。
      markPurchaseSurfaceSeen();
      // 文脈モーダルは従来の pt不足シートを「置き換える」ものなので、露出そのものは
      // 変えない。この2イベントは出し分けに関わらず従来どおり同じ条件で立てる
      // （露出を固定したまま「見せ方」の効果だけを分離して測るため）。
      trackFirebaseEvent(FIREBASE_EVENT_NAME.PT_ZERO_REACHED, {
        last_action: callType === "voice" ? "voicecall" : "videocall",
      });
      trackFirebaseEvent(FIREBASE_EVENT_NAME.PAYWALL_VIEW, {
        trigger: "pt_zero",
      });

      // ビデオ通話が pt 不足で自動終了した、登録7日以内の未課金ユーザーには
      // 汎用ポイントストアではなく相手の名前と顔で出す（Phase 1）。
      // 課金経験が判定できていないあいだは従来どおりのシートに倒す（安全側）。
      const callState = useCallStore.getState();
      if (
        callType === CALL_TYPE.VIDEO &&
        isCallPromptTarget &&
        callState.partnerId
      ) {
        // 再訪トリガー（チャット / プロフィール / 通話ボタン再タップ）が
        // この通話を参照できるよう、表示の可否に関わらず先に記録する。
        markPointShortageCall(callState.partnerId);
        useCallPointShortagePromptStore.getState().show({
          trigger: "call_end",
          partnerId: callState.partnerId,
          partnerName: callState.partnerName ?? "",
          partnerPhoto: getAvatarUrl(callState.partnerAvatarUrl),
          // Phase 1 は pointShortage（= detail 201）でしか通らないが、
          // 誤爆したときに気づけるよう実際の値を計測に載せる。
          callEndDetail: mapCallEndReasonToEvent(phase.reason)
            .call_ended_event_detail,
          callType: CALL_TYPE.VIDEO,
          balancePt: callState.currentPoint ?? undefined,
          callDurationSec: callState.callDurationSec,
        });
        return;
      }

      useInsufficientPointSheetStore.getState().show({
        needed: {
          label: TEXT.insufficientPointSheet.neededLabelCall,
          value:
            callType === "voice"
              ? MIN_POINT_FOR_VOICE_CALL
              : MIN_POINT_FOR_VIDEO_CALL,
          unit: TEXT.insufficientPointSheet.unitPerMin,
          scene: callType === "voice" ? "voice" : "video",
        },
        onClose: () => useCallStore.getState().reset(),
      });
    }
  }, [phase, location, callType, isCallPromptTarget]);

  const BOTTOM_NAV_VISIBLE_PATHS = [
    "/discover",
    "/recommend",
    "/live",
    "/board",
    "/messages",
    "/my-page",
    // coco+ の下部ナビ「お気に入り」（推しの近況）
    "/favorites",
  ];

  const showBottomNav = BOTTOM_NAV_VISIBLE_PATHS.some(
    (p) => location.pathname === p || location.pathname === `${p}/`,
  );
  // 直前に開いていたタブの画面を覚える（「他の動画を見る」のギャラリー案内を、タブの無い画面で閉じたときの移り先）
  // （ルーターから取った location の値。画面が変わると再描画される）
  const currentPath = location.pathname;
  useEffect(() => {
    if (showBottomNav) {
      useLastNavTabStore.getState().setPath(currentPath.replace(/\/$/, ""));
    }
  }, [showBottomNav, currentPath]);

  const isCallEnded =
    phase.type === "ended" && !location.pathname.startsWith("/call");

  const autumnPointBackCallResult = useAutumnPointBackCallResultStore(
    (s) => s.result,
  );
  const hideAutumnPointBackCallResult = useAutumnPointBackCallResultStore(
    (s) => s.hide,
  );

  const handleCloseEndedModal = () => {
    useCallStore.getState().reset();
  };

  const handleFavorite = () => {
    // TODO: お気に入り API 実装後に接続
  };

  // coco+ は専用の通話終了モーダル（⭐ でお気に入り登録できる）。
  // 発信が繋がらず他の女性を出すとき（fallback）は従来のモーダルのまま
  const cocoplusEndedReason =
    isCocoplusApp() &&
    !callFallback &&
    isCallEnded &&
    phase.type === "ended" &&
    phase.reason !== "pointShortage"
      ? phase.reason
      : null;
  const cocoplusEndedFavorite = useCocoplusCallEndedFavorite(
    cocoplusEndedReason ? partnerId : null,
  );

  const handleCallRequest = async () => {
    if (isSendingCallRequest) return;
    const id = partnerId ?? "";
    if (!id) return;
    const ok = await sendCallRequest({
      partnerId: id,
      partnerName: partnerName ?? "",
    });
    if (ok) useCallStore.getState().reset();
  };

  const handleVoiceCallRequest = async () => {
    if (isSendingCallRequest) return;
    const id = partnerId ?? "";
    if (!id) return;
    const ok = await sendCallRequest({
      partnerId: id,
      partnerName: partnerName ?? "",
      callType: CALL_TYPE.VOICE,
    });
    if (ok) useCallStore.getState().reset();
  };

  // pt不足の先回りモーダルの CTA。計測はストアが持ち、ここはチャージシートを開くだけ。
  const handleSendMessage = () => {
    const id = partnerId ?? "";
    useCallStore.getState().reset();
    navigate({
      to: "/messages/$conversationId",
      params: { conversationId: id },
    });
  };

  return (
    <div>
      <Outlet />
      {showBottomNav && <BottomNav />}
      <CallOverlay />
      <DemoCallOverlay />
      <ErrorModal />
      {/* pt不足の先回りモーダル。チャット滞在／相手プロフィール滞在／いいねの
          3トリガーから開くため、feature ではなくここに1つだけ置く。購入結果の
          ネイティブイベントを受け取るため、閉じたあともストアに文脈が残っているあいだはマウントを維持する。 */}
      {ptPromptDisplayed && <PointShortagePromptHost />}
      {/* 通話 pt不足の文脈モーダル（Carina・登録7日以内・未課金のビデオ通話のみ）。
          購入結果のネイティブイベントを受け取る必要があるため、閉じたあとも
          ストアに文脈が残っているあいだはマウントを維持する。 */}
      {callPromptContext && <CallPointShortagePromptHost />}
      {/* 動画終了の先回り購入モーダル（Carina・登録7日以内・未課金。2 本目の動画を見終わって
          次の 1 本が開けなくなった瞬間）。購入成功でギャラリーへ遷移する。購入結果の
          ネイティブイベントを受け取るため、閉じたあともストアに文脈が残っているあいだはマウントを維持する。 */}
      {mediaPromptContext && <MediaPointShortagePromptHost />}
      {/* 「他の動画を見る」先回りシート（Carina・登録7日以内・未課金。1 本目の動画のあと 130pt 残したまま
          アプリに 1 分とどまった）。購入確認とビューアーをこの中で開くので、終わるまでマウントを維持する。 */}
      {nextVideoPromptContext && <MediaNextVideoPromptHost />}
      {/* ルート進入前の先回りモーダル（Carina・登録 24 時間以内・未課金・未消費。消費直前／物色の行動のあと
          2 分止まった）。ビューアー・権限ダイアログをこの中で出すので、終わるまでマウントを維持する。 */}
      {prerouteContext && <PreroutePromptHost />}
      {/* 動画の先回りシートを何も押さずに閉じた人に出す「ギャラリーもあるよ」（出すのは useOpenGalleryGuide） */}
      <MediaGalleryGuideHost />
      {/* push 通知許諾の事前案内（Carina / Sakura）。通話オーバーレイより前面 */}
      <PushSoftAskSheet />
      <HokageMigrationNoticeModal />
      {cocoplusEndedReason && (
        <CocoplusCallEndedModal
          reason={cocoplusEndedReason}
          partnerName={partnerName}
          partnerAvatarUrl={partnerAvatarUrl}
          isFavorite={cocoplusEndedFavorite.isBookmarked}
          onClose={handleCloseEndedModal}
          onFavorite={cocoplusEndedFavorite.toggleFavorite}
          onVideoCall={handleCallRequest}
          onVoiceCall={handleVoiceCallRequest}
          onSendMessage={handleSendMessage}
        />
      )}
      {!cocoplusEndedReason &&
        isCallEnded &&
        phase.type === "ended" &&
        phase.reason !== "pointShortage" && (
          <CallEndedModal
            reason={phase.reason}
            partnerName={partnerName}
            partnerAvatarUrl={partnerAvatarUrl}
            onClose={handleCloseEndedModal}
            onFavorite={handleFavorite}
            onVideoCall={handleCallRequest}
            onVoiceCall={handleVoiceCallRequest}
            onSendMessage={handleSendMessage}
            fallback={
              callFallback
                ? {
                    partnerId: callFallback.partnerId,
                    trigger: callFallback.trigger,
                    callType: callFallback.callType,
                    onSelectUser: callFallback.handleSelectUser,
                  }
                : undefined
            }
          />
        )}
      {/* ビデオ通話終了直後の秋のポイントバック進捗アニメーション。通話終了ダイアログより前面 */}
      {autumnPointBackCallResult && (
        <AutumnPointBackCallResultOverlay
          result={autumnPointBackCallResult}
          onClose={hideAutumnPointBackCallResult}
        />
      )}
      {/* ニュースモーダル（settings / discover から store 経由で開く）。
          開いている間だけマウントする。 */}
      {newsModalOpen && <NewsModal />}
      {/* 新規登録ボーナス（250pt）付与アニメーション。開いている間だけマウントする。 */}
      {signupBonusOpen && <SignupBonusModal />}
      {/* ポイントクイックチャージシート（call / live から store 経由で開く）。
          常時マウントだと useUserPoint が走り続けるため、開いている間だけマウントする。
          open={false} でも退場アニメ中は描画し続ける（useDelayedUnmount）。 */}
      {quickChargeRender && (
        <PurchaseQuickChargeSheet
          open={quickChargeOpen}
          onClose={hideQuickCharge}
          source={quickChargeSource}
          scene={quickChargeScene}
          onChargeSuccess={(points) => quickChargeOnSuccess?.(points)}
        />
      )}
      {/* ポイント不足シート（IAP その場チャージ）。ライブ入室・メッセージ送信など
          複数 feature から useInsufficientPointSheetStore 経由で開く。
          開いている間だけマウントして useUserPoint / useStartPurchase が常時起動しないようにする。
          open={false} でも退場アニメ中は描画し続ける（useDelayedUnmount）。 */}
      {insufficientSheetRender && insufficientSheetNeeded && (
        <InsufficientPointSheet
          open={insufficientSheetOpen}
          needed={insufficientSheetNeeded}
          shakeSignal={insufficientSheetShakeSignal}
          onClose={() => {
            insufficientSheetOnClose?.();
            hideInsufficientSheet();
          }}
          onChargeSuccess={(points) => {
            insufficientSheetOnSuccess?.(points);
            hideInsufficientSheet();
          }}
        />
      )}
      {/* Coco+ の通話中（ビデオ・音声）pt 不足の買い足しシート。通話画面の上に重ねる。
          開いている間（と退場アニメのぶん）だけマウントする。 */}
      {callLowPointRender && callLowPointScene && (
        <CallLowPointSheet
          open={callLowPointOpen}
          scene={callLowPointScene}
          onClose={hideCallLowPoint}
          onChargeSuccess={(points) => {
            callLowPointOnSuccess?.(points);
            hideCallLowPoint();
          }}
        />
      )}
      {/* ポイント不足の控えめな通知バナー（通話中・ライブ視聴中に自動表示）。
          タップで上のポイント不足シートを開くか、右スワイプで見送るまで自動的には消えない。
          開いている間だけマウントする。 */}
      {insufficientNoticeRender && (
        <InsufficientPointNotice
          visible={insufficientNoticeVisible}
          onTap={() => insufficientNoticeOnTap?.()}
          onSwipeDismiss={hideInsufficientNotice}
        />
      )}
    </div>
  );
};

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: () => {
    const token = useAuthStore.getState().token;
    if (!token) {
      throw redirect({ to: "/login" });
    }
  },
  component: AuthenticatedLayout,
});
